import { Component, OnDestroy, OnInit } from '@angular/core';
import { Router } from "@angular/router";
import { of, Subject } from "rxjs";
import {catchError, finalize, switchMap, takeUntil, tap} from "rxjs/operators";
import { MessageService } from "primeng/api";

// Services / Subjects
import { FeatureService } from "../../../services/feature.service";
import { FileService } from "../../../services/file.service";
import { PropertyService } from "../../../services/property.service";
import { PropertyInitialSubject } from "../../../subject/property.initial.subject";
import { FlowModeSubject } from "../../../subject/flow.mode.subject";
import { FlowService } from "../../../services/flow.service";
import { FlowStateService } from "../../../services/flow.state.service";

// DTOs / Enums
import { PropertyInitialResponseDto } from "../../../dto/property.initial.response.dto";
import { FlowMode } from "../../../dto/enum/flow.mode.enum";
import { RecordStageEnum } from "../../../dto/enum/record.stage.enum";

const STEP_KEY = 'reviews';

@Component({
    selector: 'app-property-pictures-reviews',
    templateUrl: './property-pictures-reviews.component.html',
    styleUrls: ['./property-pictures-reviews.component.scss']
})
export class PropertyPicturesReviewsComponent implements OnInit, OnDestroy {
    // --------- Flujo / navegación -------------
    currentMode: FlowMode = FlowMode.CREATE;
    previousUrl = '';
    nextUrl = '';

    // --------- Propiedad / backend -------------
    propertyInitial: PropertyInitialResponseDto | null = null;
    propertyId: number | undefined;
    segmentLabel: string | undefined = '';

    // --------- UI / estado -------------
    loading = true;
    showErrorDialog = false;
    dialogCode: string = "TADIC-0001";
    dialogMessage: string = "Debe seleccionar una imagen para continuar.";
    private destroy$ = new Subject<void>();

    // --------- Datos del paso -------------
    imageCategories: { images: any[]; label: string; mandatory: boolean; featureId: string }[] = [];
    selectedImage: { id: number, name: string, url: string } | null = null;

    constructor(
        private router: Router,
        private featureService: FeatureService,
        private fileService: FileService,
        private propertyService: PropertyService,
        private propertyInitialSubject: PropertyInitialSubject,
        private flowModeSubject: FlowModeSubject,
        private flowService: FlowService,
        private flowState: FlowStateService,
        private messageService: MessageService
    ) {}

    // -----------------------------------------------------------
    // Ciclo de vida
    // -----------------------------------------------------------
    ngOnInit(): void {
        // 1) Modo y rutas
        const mode = this.flowModeSubject.getCurrentMode();
        if (!mode) { this.router.navigate(['/list-pending']); return; }
        this.currentMode = mode;
        this.previousUrl = this.flowService.getPreviousStep(STEP_KEY); // pictures -> reviews
        this.nextUrl = this.flowService.getNextStep(STEP_KEY);        // reviews -> tags

        // 2) Paso actual
        this.flowState.setCurrentStep(STEP_KEY);

        // 3) Propiedad y stage
        this.propertyInitial = this.propertyInitialSubject.getCurrentProperty();
        if (!this.propertyInitial?.propertyId) {
            this.router.navigate(['/list-pending']);
            return;
        }
        this.propertyId = this.propertyInitial.propertyId;
        this.segmentLabel = this.propertyInitial.segmentLabel || 'propiedad';
        if (this.propertyInitial.recordStage) {
            this.flowState.setRecordStage(this.propertyInitial.recordStage as RecordStageEnum);
        }
        this.flowState.setPropertyId(this.propertyId);

        // 4) Cargar imágenes y restaurar cache de portada (si existe)
        this.loadUploadedImages();
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    // -----------------------------------------------------------
    // Carga de imágenes
    // -----------------------------------------------------------
    private loadUploadedImages(): void {
        if (!this.propertyId) return;
        this.loading = true;

        this.featureService.getMultimediaPropertyList(this.propertyId).pipe(
            tap((response: any[]) => {
                // 🔁 En vez de flatMap, usamos reduce para aplanar
                const validImages = (response || []).reduce((acc: any[], feature: any) => {
                    const files = (feature.fileList || [])
                        .filter((f: any) => f.ctFileType === 'IMAGE_FILE')
                        .map((f: any) => ({
                            fileCategory: feature.featureLabel,
                            fileId: f.fileId,
                            fileTitle: f.fileTitle || f.fileCategory || `Imagen ${f.fileId}`,
                            fileUrl: f.fileUrl
                        }));
                    return acc.concat(files);
                }, []);

                // Agrupar por categoría
                const grouped: Record<string, any[]> = {};
                validImages.forEach(img => {
                    grouped[img.fileCategory] = grouped[img.fileCategory] || [];
                    grouped[img.fileCategory].push({ id: img.fileId, name: img.fileTitle, url: img.fileUrl });
                });

                this.imageCategories = Object.entries(grouped).map(([category, images]) => ({
                    featureId: category,
                    label: category,
                    mandatory: category.toLowerCase() === 'fachada',
                    images
                }));

                // restaurar portada del cache si existía
                const cached = this.flowState.getStepData('reviews');
                const coverId = cached?.coverId as number | undefined;
                if (coverId) {
                    const found = this.findImageById(coverId);
                    if (found) this.selectedImage = found;
                }
            }),
            finalize(() => (this.loading = false)),
            catchError((err) => {
                console.error('Error al cargar imágenes:', err);
                this.imageCategories = [];
                this.showError('TADIC-IMAGES', 'No se pudieron cargar las imágenes.');
                return of([]);
            }),
            takeUntil(this.destroy$)
        ).subscribe();
    }


    private findImageById(id: number): { id: number, name: string, url: string } | null {
        for (const cat of this.imageCategories) {
            const found = (cat.images || []).find((i: any) => i.id === id);
            if (found) return found;
        }
        return null;
    }

    // -----------------------------------------------------------
    // UI
    // -----------------------------------------------------------
    selectImage(image: any): void {
        this.selectedImage = image;
        this.persistToFlowState();
        this.showErrorDialog = false;
    }

    private persistToFlowState(): void {
        this.flowState.setStepData(STEP_KEY, {
            coverId: this.selectedImage?.id ?? null
        });
    }

    // -----------------------------------------------------------
    // Navegación / Guardado / Avance de etapa
    // -----------------------------------------------------------
    handleNextStep(): void {
        if (!this.selectedImage) {
            this.showError('TADIC-0001', 'Debe seleccionar una imagen para continuar.');
            return;
        }

        // PUT marcar portada
        const body = { id: this.selectedImage.id };
        this.fileService.putFileFirst(body).pipe(
            tap(() => {
                // marcar stage local (seguimos en MULTIMEDIA en ausencia de stage intermedio específico)
                this.flowState.setRecordStage(RecordStageEnum.MULTIMEDIA);
            }),
            switchMap(() => {
                if (this.shouldAdvanceStage()) {
                    const nextBody = { id: this.propertyId };
                    return this.propertyService.nextStageProperty(nextBody).pipe(
                        catchError((err) => { console.error('nextStage error', err); return of(null); })
                    );
                }
                return of(null);
            }),
            takeUntil(this.destroy$)
        ).subscribe(() => this.router.navigate([this.nextUrl]));
    }

    handleSaveAndExit(): void {
        if (!this.selectedImage) {
            this.showError('TADIC-0001', 'Debe seleccionar una imagen para continuar.');
            return;
        }
        const body = { id: this.selectedImage.id };
        this.fileService.putFileFirst(body).pipe(
            catchError((err) => { console.error('save cover error', err); return of(null); }),
            takeUntil(this.destroy$)
        ).subscribe(() => this.router.navigate(['/list-pending']));
    }

    handlePreviousStep(): void {
        this.router.navigate([this.previousUrl]);
    }

    // Política:
    // - CREATE → avanza
    // - EDIT → no avanza
    // - RESUME → avanza sólo si currentIdx > stageIdx
    private shouldAdvanceStage(): boolean {
        if (this.currentMode === FlowMode.EDIT) return false;
        if (this.currentMode === FlowMode.CREATE) return true;

        const stage = this.flowState.recordStage;
        if (!stage) return false;

        const stageStep = this.flowState.getStepByRecordStage(stage); // MULTIMEDIA -> 'pictures'
        const currentIdx = this.flowService.getStepIndex(STEP_KEY);   // 'reviews'
        const stageIdx = this.flowService.getStepIndex(stageStep);

        if (currentIdx === -1 || stageIdx === -1) return false;
        return currentIdx > stageIdx;
    }

    // -----------------------------------------------------------
    // Errores
    // -----------------------------------------------------------
    private showError(code: string, msg: string): void {
        this.showErrorDialog = true;
        this.dialogCode = code;
        this.dialogMessage = msg;
        this.messageService.add({ severity: 'error', summary: code, detail: msg });
    }
}
